import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { examAreaQueries } from '@/data/query-keys/examAreaQueryKey';
import {
  createExamArea,
  deleteExamArea,
  reorderExamAreas,
} from '@/data/services/examAreaServices';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';

type UseExamAreasProps = {
  examId: number;
};

const useExamAreas = ({ examId }: UseExamAreasProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.settings',
  });

  const {
    results: examAreas,
    isInitialLoading,
    cacheManager,
  } = useListService({
    ...examAreaQueries.list({ examId, pageSize: ORDENABLE_PAGE_SIZE }),
    enabled: !!examId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (areaId: number) => createExamArea({ examId, areaId }),
    onSuccess: examArea => {
      cacheManager.add(examArea);
      alert.success(t('attachSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExamArea,
    onSuccess: (_, examAreaId) => {
      cacheManager.remove(examAreaId);
      alert.success(t('detachSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (examAreaIds: number[]) => {
      const reordered = reorder(examAreas, examAreaIds);
      cacheManager.set(reordered);
    },
    mutationFn: (examAreaIds: number[]) =>
      reorderExamAreas(examId, examAreaIds),
    onSuccess: () => {
      alert.success(t('reorderSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    examAreas,
    cacheManager,
    isLoading: isInitialLoading,
    attachArea: createMutation.mutate,
    attachingAreaId: createMutation.isPending ? createMutation.variables : null,
    detachArea: deleteMutation.mutateAsync,
    detachingExamAreaId: deleteMutation.isPending
      ? deleteMutation.variables
      : null,
    reorderAreas: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useExamAreas;
